import { useState, useRef, useCallback, useEffect } from 'react';
import axios from 'axios';
import { config } from '../../configs/app.config';

const API_KEY = config.google.apiKey;

const fetchSuggestions = async (input, locationBias) => {
  const body = {
    input,
    includedPrimaryTypes: ['street_address', 'premise', 'subpremise', 'route'],
  };

  if (locationBias) {
    body.locationBias = locationBias;
  }

  const response = await axios.post(
    'https://places.googleapis.com/v1/places:autocomplete',
    body,
    {
      headers: {
        'Content-Type': 'application/json',
        'X-Goog-Api-Key': API_KEY,
      },
      transformRequest: (data, headers) => {
        delete headers.common['Authorization'];
        return JSON.stringify(data);
      },
      withCredentials: false,
      timeout: 10000,
    },
  );

  return (response.data.suggestions || [])
    .filter((s) => s.placePrediction)
    .map((s) => ({
      description: s.placePrediction.text?.text || '',
      placeId: s.placePrediction.placeId,
    }));
};

export const fetchPlaceDetails = async (placeId) => {
  const response = await axios.get(
    `https://places.googleapis.com/v1/places/${placeId}?fields=addressComponents,location&key=${API_KEY}`,
    {
      transformRequest: (data, headers) => {
        delete headers.common['Authorization'];
        return data;
      },
      withCredentials: false,
      timeout: 120 * 1000,
    },
  );
  return response;
};

const usePlacesAutocomplete = ({
  debounce = 500,
  minLength = 3,
  locationBias,
} = {}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const timerRef = useRef(null);

  const search = useCallback(
    (input) => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }

      if (!input || input.length < minLength) {
        setSuggestions([]);
        return;
      }

      timerRef.current = setTimeout(async () => {
        try {
          setLoading(true);
          const results = await fetchSuggestions(input, locationBias);
          setSuggestions(results);
        } catch (error) {
          console.error('Places autocomplete error:', error);
          setSuggestions([]);
        } finally {
          setLoading(false);
        }
      }, debounce);
    },
    [debounce, minLength, locationBias],
  );

  const clearSuggestions = useCallback(() => {
    setSuggestions([]);
  }, []);

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  return { suggestions, loading, search, clearSuggestions };
};

export default usePlacesAutocomplete;
